import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authClient } from '../lib/authClient';
import PasswordInput from '../components/PasswordInput';
import ResendVerification from '../components/ResendVerification';
import { afterSignInPage } from '../utils/pendingInvite';

export default function SignUp() {
  const navigate = useNavigate();
  const { refetch: refetchSession } = authClient.useSession();
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  // The address a confirmation link went to. A registered address shows the same screen (its
  // owner gets an email instead), so the form never reveals who has an account.
  const [sentTo, setSentTo]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data, error: authError } = await authClient.signUp.email({ name, email, password, callbackURL: '/email-verified' });
      if (authError?.status === 429) {
        setError('Too many attempts, try again in a few minutes.');
      } else if (authError) {
        setError(authError.message || 'Could not create account.');
      } else if (!data?.token) {
        setSentTo(email.trim());
      } else {
        // Signed in already (email confirmation switched off with EMAIL_VERIFICATION_REQUIRED=false).
        // Load the new session before leaving: otherwise the dashboard's guard can still see the
        // old "signed out" state and bounce straight back here with an empty form
        await refetchSession();
        navigate(afterSignInPage());
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startAgain = () => {
    setSentTo('');
    setPassword('');
    setConfirm('');
  };

  if (sentTo) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Check your inbox</h1>
          <p style={styles.text}>
            We've sent a link to <strong>{sentTo}</strong>. Open it to finish setting up; it expires in an hour.
          </p>
          <p style={styles.text}>Nothing there after a few minutes? Check your spam folder, or:</p>
          <ResendVerification email={sentTo} label="Resend email" />
          <p style={styles.footer}>
            Wrong email? <button type="button" onClick={startAgain} style={styles.linkButton}>Start again</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Create account</h1>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            autoComplete="name"
            placeholder="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            style={styles.input}
          />
          <input
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={styles.input}
          />
          <PasswordInput
            autoComplete="new-password"
            placeholder="Password (at least 8 characters)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
            style={styles.input}
          />
          <PasswordInput
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            minLength={8}
            style={styles.input}
          />
          {error && <p style={styles.error}>{error}</p>}
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p style={styles.footer}>
          Already have an account? <Link to="/sign-in" style={styles.link}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  title: {
    margin: '0 0 24px',
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center',
    color: '#1a1a1a',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  input: {
    padding: '12px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 15,
    outline: 'none',
  },
  error: {
    color: '#dc3545',
    fontSize: 14,
    margin: 0,
  },
  button: {
    padding: '12px',
    borderRadius: 8,
    border: 'none',
    background: '#667eea',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  footer: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
    color: '#666',
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: 600,
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#667eea',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  text: {
    fontSize: 14,
    color: '#444',
    lineHeight: 1.5,
    margin: '0 0 16px',
  },
};
